import { Injectable, signal } from '@angular/core';
import { VaultSyncWriter } from './vault-sync.tokens';
import { Log } from '../../core/log';
import { IS_ANDROID_WEB_VIEW } from '../../util/is-android-web-view';

/**
 * Capacitor (Android) implementation of VaultSyncWriter.
 *
 * Uses @capacitor/filesystem with Directory.External, which on Android
 * maps to /sdcard/Android/data/<applicationId>/files/ — a per-app
 * sandboxed area that survives uninstall only with backup-rules enabled.
 *
 * For external-visible storage (so the user can open the .md files in
 * Obsidian on the same device), the v0.2 setting will offer
 * Directory.ExternalStorage which requires the MANAGE_EXTERNAL_STORAGE
 * permission. v0.1 uses the safer Directory.External default.
 *
 * Watching: Capacitor Filesystem has no native watcher; we poll on
 * focus + on demand (Sprint 3 deferred).
 */
@Injectable()
export class VaultSyncWriterCapacitor implements VaultSyncWriter {
  readonly vaultPath = signal<string>('Septaine-vault');

  isSupported(): boolean {
    // Only Capacitor + Android — iOS untested per feedback_dont_ship_untestable.
    return IS_ANDROID_WEB_VIEW;
  }

  async writeFile(relativePath: string, content: string): Promise<void> {
    if (!this.isSupported()) {
      throw new Error('[vault-sync] Capacitor not detected');
    }
    const fs = await this._fs();
    if (!fs) throw new Error('[vault-sync] @capacitor/filesystem unavailable');
    const dir = this.vaultPath();
    const path = `${dir}/${relativePath}`;
    // Ensure parent dir exists.
    const parent = path.substring(0, path.lastIndexOf('/'));
    if (parent) {
      try {
        await fs.mkdir({
          path: parent,
          directory: 'EXTERNAL',
          recursive: true,
        });
      } catch (e) {
        // Ignore "already exists"
        const msg = (e as Error).message ?? '';
        if (!/exists/i.test(msg)) {
          Log.warn({ id: 'vault-sync.mkdir-warn', msg });
        }
      }
    }
    await fs.writeFile({
      path,
      data: content,
      directory: 'EXTERNAL',
      encoding: 'utf8',
    });
  }

  async readFile(relativePath: string): Promise<string | null> {
    if (!this.isSupported()) return null;
    const fs = await this._fs();
    if (!fs) return null;
    try {
      const result = await fs.readFile({
        path: `${this.vaultPath()}/${relativePath}`,
        directory: 'EXTERNAL',
        encoding: 'utf8',
      });
      return typeof result.data === 'string' ? result.data : null;
    } catch {
      return null;
    }
  }

  async deleteFile(relativePath: string): Promise<void> {
    if (!this.isSupported()) return;
    const fs = await this._fs();
    if (!fs) return;
    try {
      await fs.deleteFile({
        path: `${this.vaultPath()}/${relativePath}`,
        directory: 'EXTERNAL',
      });
    } catch (e) {
      // Treat "doesn't exist" as success — idempotent delete.
      Log.warn({
        id: 'vault-sync.delete-mobile-warn',
        msg: (e as Error).message ?? '',
      });
    }
  }

  watch(
    _handler: (relativePath: string, change: 'created' | 'modified' | 'deleted') => void,
  ): () => void {
    // Capacitor Filesystem has no watcher. Sprint 3 will add a poll-on-focus
    // implementation. For now, watch is a no-op.
    return () => undefined;
  }

  private async _fs(): Promise<any | null> {
    try {
      const mod = await import('@capacitor/filesystem');
      return mod.Filesystem;
    } catch {
      return null;
    }
  }
}
